/**
 * AddPropertyWizardPage
 * Full-page wizard at /properties/new — replaces PropertiesPage's former two-step create
 * Modal. Layout chrome ported from lease-wizard/LeaseWizardPage.tsx, adapted to this
 * wizard's short (1-4 step) flow. See docs/features/core-property-crud/slices for AC-W1..W7.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTrackEvent, ANALYTICS_EVENTS } from '@resida/analytics';
import { createPropertyUnitSchema, type CreatePropertyRequest } from '@resida/api-types/leasing';
import {
  listPortfolios,
  createProperty,
  type PortfolioWithStats,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { AddressAutocompleteInput, type AddressSuggestion } from '../components/AddressAutocompleteInput';

const PRIMARY_TYPES = [
  { value: 'single_family', label: 'Single Family', description: 'One unit only' },
  { value: 'multi_family', label: 'Multi Family', description: 'Multiple units' },
] as const;

const SUB_TYPES = [
  { value: '', label: 'None', description: 'No specific sub-type' },
  { value: 'house', label: 'House', description: 'Standalone house' },
  { value: 'condo', label: 'Condominium', description: 'Individual condo unit(s)' },
  { value: 'townhouse', label: 'Townhouse', description: 'Attached home(s)' },
  { value: 'apartment', label: 'Apartment', description: 'Apartment unit(s)' },
  { value: 'other', label: 'Other', description: 'Other property sub-type' },
] as const;

type WizardStep = 'type' | 'info' | 'address' | 'details';

const STEP_LABELS: Record<WizardStep, string> = {
  type: 'Property Type',
  info: 'Property Info',
  address: 'Address',
  details: 'Details',
};

interface PropertyFormData {
  propertyType: 'single_family' | 'multi_family' | '';
  portfolioId: string;
  name: string;
  subType: string;
  yearBuilt: string;
  squareFeet: string;
  unitAddress1: string;
  unitAddress2: string;
  unitCity: string;
  unitState: string;
  unitZipCode: string;
  bedrooms: string;
  bathrooms: string;
  notes: string;
}

const initialFormData: PropertyFormData = {
  propertyType: '',
  portfolioId: '',
  name: '',
  subType: '',
  yearBuilt: '',
  squareFeet: '',
  unitAddress1: '',
  unitAddress2: '',
  unitCity: '',
  unitState: '',
  unitZipCode: '',
  bedrooms: '',
  bathrooms: '',
  notes: '',
};

type FormField = keyof PropertyFormData;
type FieldErrors = Partial<Record<FormField, string>>;

/**
 * The form fields that become the Unit in a single_family create, with the Unit schema key each
 * one is validated under and the wizard step that holds it. Client validation reuses the request
 * schema (so limits and copy cannot drift from the server), and server field errors use this map
 * to find the step and field to send the user back to.
 */
const UNIT_FIELDS: Partial<Record<FormField, { key: string; step: WizardStep }>> = {
  unitAddress1: { key: 'address1', step: 'address' },
  unitAddress2: { key: 'address2', step: 'address' },
  unitCity: { key: 'city', step: 'address' },
  unitState: { key: 'state', step: 'address' },
  unitZipCode: { key: 'zipCode', step: 'address' },
  bedrooms: { key: 'bedrooms', step: 'details' },
  bathrooms: { key: 'bathrooms', step: 'details' },
  squareFeet: { key: 'sqft', step: 'details' },
};

function fieldForUnitKey(key: string): FormField | undefined {
  return (Object.keys(UNIT_FIELDS) as FormField[]).find((field) => UNIT_FIELDS[field]?.key === key);
}

/** The Unit a single_family create carries (BR-9): its address, details and metadata. */
function buildUnitPayload(form: PropertyFormData): NonNullable<CreatePropertyRequest['unit']> {
  const metadata: Record<string, unknown> = {};
  if (form.notes.trim()) metadata.notes = form.notes.trim();
  if (form.yearBuilt) metadata.constructionYear = Number(form.yearBuilt);
  return {
    address1: form.unitAddress1.trim(),
    address2: form.unitAddress2.trim() || undefined,
    city: form.unitCity.trim(),
    state: form.unitState.trim(),
    zipCode: form.unitZipCode.trim(),
    bedrooms: form.bedrooms ? Number(form.bedrooms) : undefined,
    bathrooms: form.bathrooms || undefined,
    sqft: form.squareFeet ? Number(form.squareFeet) : undefined,
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  };
}

/**
 * Limit violations in what the user has typed, from the request's own schema. A field that is
 * still empty is not reported here: the step's required-field gate already holds Next back.
 */
function validateUnitFields(form: PropertyFormData): FieldErrors {
  const errors: FieldErrors = {};
  const result = createPropertyUnitSchema.safeParse(buildUnitPayload(form));
  if (result.success) return errors;
  for (const issue of result.error.issues) {
    const field = fieldForUnitKey(String(issue.path[0]));
    if (field && !errors[field] && form[field].trim()) errors[field] = issue.message;
  }
  return errors;
}

// The API reports a failed request-schema check as "<body/unit/field> <message>" entries.
const SERVER_UNIT_FIELD_ERROR = /(?:body\/)?unit[/.](\w+):?\s+(.+?)(?=,\s*(?:body\/)?unit[/.]|$)/g;

function parseServerFieldErrors(message: string): FieldErrors {
  const errors: FieldErrors = {};
  for (const match of message.matchAll(SERVER_UNIT_FIELD_ERROR)) {
    const field = fieldForUnitKey(match[1] ?? '');
    if (field && !errors[field]) errors[field] = (match[2] ?? '').trim();
  }
  return errors;
}

/** Drop request-schema paths ("body/name ...") so a raw server message reads as plain copy. */
function stripSchemaPaths(message: string): string {
  return message.replace(/\bbody\/[\w/]+\s+/g, '');
}

export function AddPropertyWizardPage() {
  const navigate = useNavigate();
  const track = useTrackEvent();
  const [searchParams] = useSearchParams();
  const selectedPortfolioId = searchParams.get('portfolioId') || '';

  const [portfolios, setPortfolios] = useState<PortfolioWithStats[]>([]);
  const [formData, setFormData] = useState<PropertyFormData>(initialFormData);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<FieldErrors>({});
  const [portfoliosError, setPortfoliosError] = useState(false);
  const [isLoadingPortfolios, setIsLoadingPortfolios] = useState(true);
  const pendingFocusRef = useRef<FormField | null>(null);

  const loadPortfolios = useCallback(() => {
    setIsLoadingPortfolios(true);
    listPortfolios()
      .then((res) => {
        setPortfoliosError(false);
        setPortfolios(res.portfolios);
        setFormData((prev) => ({
          ...prev,
          portfolioId: prev.portfolioId || selectedPortfolioId || (res.portfolios[0]?.id || ''),
        }));
      })
      .catch(() => setPortfoliosError(true))
      .finally(() => setIsLoadingPortfolios(false));
  }, [selectedPortfolioId]);

  useEffect(() => {
    loadPortfolios();
  }, [loadPortfolios]);

  // Active step list is derived from the chosen property type, not a fixed enum position —
  // Address/Details never render for multi_family (AC-W3), and 'formData' is never reset
  // between steps, so Back always preserves values (AC-W7).
  const steps: WizardStep[] = useMemo(() => {
    if (!formData.propertyType) return ['type'];
    if (formData.propertyType === 'single_family') return ['type', 'info', 'address', 'details'];
    return ['type', 'info'];
  }, [formData.propertyType]);

  const currentStep: WizardStep = steps[Math.min(currentStepIndex, steps.length - 1)] ?? 'type';
  // Before a type is chosen, steps is the single-item placeholder ['type'] — that is never
  // "the last step" in the submit sense (the type cards advance past it directly, AC-W1/W2).
  const isLastStep = currentStep !== 'type' && currentStepIndex >= steps.length - 1;

  const clearServerFieldErrors = (fields: FormField[]) => {
    setServerFieldErrors((prev) => {
      if (!fields.some((field) => field in prev)) return prev;
      const next = { ...prev };
      for (const field of fields) delete next[field];
      return next;
    });
  };

  const handleFormChange = (field: FormField, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearServerFieldErrors([field]);
  };

  const handleAddressSuggestionSelect = (suggestion: AddressSuggestion) => {
    setFormData((prev) => ({
      ...prev,
      unitAddress1: suggestion.address1,
      unitCity: suggestion.city,
      unitState: suggestion.state,
      unitZipCode: suggestion.zipCode,
    }));
    clearServerFieldErrors(['unitAddress1', 'unitCity', 'unitState', 'unitZipCode']);
  };

  const handleTypeSelect = (type: CreatePropertyRequest['propertyType']) => {
    setFormData((prev) => ({ ...prev, propertyType: type }));
    setCurrentStepIndex(1); // 'info' is always index 1 for both step lists
  };

  // A rejected field is shown on the step that holds it, marked invalid with the server's own
  // sentence; any other failure reads as plain copy in the banner.
  const handleCreateError = (message: string) => {
    const errors = parseServerFieldErrors(message);
    const fields = (Object.keys(errors) as FormField[]).sort(
      (a, b) => steps.indexOf(UNIT_FIELDS[a]!.step) - steps.indexOf(UNIT_FIELDS[b]!.step),
    );
    const firstField = fields[0];
    if (!firstField) {
      setCreateError(stripSchemaPaths(message));
      return;
    }
    setServerFieldErrors(errors);
    setCreateError(null);
    pendingFocusRef.current = firstField;
    setCurrentStepIndex(Math.max(steps.indexOf(UNIT_FIELDS[firstField]!.step), 0));
  };

  const handleCreateSubmit = async () => {
    if (!formData.propertyType) {
      setCreateError('Please select a property type');
      return;
    }

    const isSingleFamily = formData.propertyType === 'single_family';
    // Year built and square feet describe the Unit on a single-family property (the Unit page
    // and disclosure logic read them there); the Property keeps a copy for its own detail page.
    const metadata: Record<string, unknown> = {};
    if (formData.yearBuilt) {
      metadata.constructionYear = Number(formData.yearBuilt);
    }
    if (formData.squareFeet) {
      metadata.squareFeet = Number(formData.squareFeet);
    }
    if (formData.subType) {
      metadata.subType = formData.subType;
    }

    const propertyData: CreatePropertyRequest = {
      portfolioId: formData.portfolioId,
      name: formData.name,
      propertyType: formData.propertyType,
      status: 'active',
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
      // A single_family property and its one Unit are created together in this request (BR-9).
      unit: isSingleFamily ? buildUnitPayload(formData) : undefined,
    };

    try {
      setIsCreating(true);
      setCreateError(null);
      setServerFieldErrors({});

      const propertyResponse = await createProperty(propertyData);

      track(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, { step: 'property' });
      navigate(`/properties/${propertyResponse.property.id}`);
    } catch (err) {
      handleCreateError(err instanceof Error ? err.message : 'Failed to create property');
    } finally {
      setIsCreating(false);
    }
  };

  const handleBack = () => {
    if (currentStepIndex === 0) {
      navigate('/properties');
    } else {
      setCreateError(null);
      setCurrentStepIndex((i) => i - 1);
    }
  };

  const handleNext = () => {
    if (isLastStep) {
      void handleCreateSubmit();
    } else {
      setCurrentStepIndex((i) => Math.min(i + 1, steps.length - 1));
    }
  };

  // Limit violations the user has typed (blocks Next) and fields the server rejected (shown
  // until that field is edited). Both only exist for the Unit fields of a single-family create.
  const clientFieldErrors = useMemo<FieldErrors>(
    () => (formData.propertyType === 'single_family' ? validateUnitFields(formData) : {}),
    [formData],
  );
  const fieldErrors: FieldErrors = { ...serverFieldErrors, ...clientFieldErrors };
  const stepHasLimitError = (Object.keys(clientFieldErrors) as FormField[]).some(
    (field) => UNIT_FIELDS[field]?.step === currentStep,
  );

  // Points at highlighted fields, so it exists only while the current step shows one: it goes
  // away when the last server field error is edited clear, and on steps with nothing highlighted.
  const hasServerErrorOnStep = (Object.keys(serverFieldErrors) as FormField[]).some(
    (field) => UNIT_FIELDS[field]?.step === currentStep,
  );
  const bannerMessage = createError
    ?? (hasServerErrorOnStep
      ? 'Some details need fixing before this property can be created. Check the highlighted fields.'
      : null);

  // After a failed create routes the user back to the owning step, put focus on the rejected field.
  useEffect(() => {
    const field = pendingFocusRef.current;
    if (!field) return;
    pendingFocusRef.current = null;
    document.getElementById(field)?.focus();
  }, [currentStep, serverFieldErrors]);

  const stepValid = !stepHasLimitError && (() => {
    switch (currentStep) {
      case 'type':
        return Boolean(formData.propertyType);
      case 'info':
        return Boolean(formData.portfolioId) && Boolean(formData.name.trim());
      case 'address':
        // Trimmed, because the create request sends trimmed values: a whitespace-only field
        // would otherwise pass here and be rejected by the server on submit.
        return [formData.unitAddress1, formData.unitCity, formData.unitState, formData.unitZipCode]
          .every((value) => Boolean(value.trim()));
      case 'details':
        return Boolean(formData.bedrooms) && Boolean(formData.bathrooms);
      default:
        return true;
    }
  })();

  const inputClass = "w-full px-3 py-2.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring box-border";
  const labelClass = "block mb-1 text-sm font-medium text-foreground";
  const inputClassFor = (field: FormField, extra = '') =>
    `${inputClass}${extra}${fieldErrors[field] ? ' border-destructive' : ''}`;
  const errorProps = (field: FormField) =>
    fieldErrors[field] ? ({ 'aria-invalid': true, 'aria-describedby': `${field}-error` } as const) : {};
  const fieldError = (field: FormField, testId: string) =>
    fieldErrors[field] ? (
      <p id={`${field}-error`} data-testid={testId} className="text-xs text-destructive mt-1 mb-0">
        {fieldErrors[field]}
      </p>
    ) : null;

  return (
    <div className="max-w-4xl mx-auto pb-28 md:pb-6" data-testid="add-property-wizard-page">
      {/* Header */}
      <div className="px-4 md:px-6 lg:px-8 pt-4 md:pt-6 lg:pt-8">
        <Link
          to="/properties"
          className="text-sm text-primary hover:text-primary/80 no-underline inline-flex min-h-[44px] items-center mb-1"
        >
          ← Back to Properties
        </Link>
        <h1 className="text-xl md:text-2xl font-semibold text-foreground m-0 mb-6">Add Property</h1>

        {/* Step indicator — compact dots on mobile, full labels on desktop. Non-interactive:
            this wizard is short enough that clickable step-pills aren't needed. */}
        <div className="mb-6">
          {/* Mobile: compact dot stepper */}
          <div className="flex items-center justify-between gap-1 md:hidden mb-2">
            <span className="text-xs font-medium text-muted-foreground">
              Step {currentStepIndex + 1}: {STEP_LABELS[currentStep]}
            </span>
            <span className="text-xs text-muted-foreground">{currentStepIndex + 1}/{steps.length}</span>
          </div>
          {/* Decorative: the "Step N: Label" and "N/M" text above is the accessible progress. */}
          <div className="flex items-center gap-1 md:hidden" aria-hidden="true" data-testid="wizard-progress-mobile">
            {steps.map((step, index) => {
              const isActive = index === currentStepIndex;
              const isCompleted = index < currentStepIndex;
              return (
                <div
                  key={step}
                  data-testid={`wizard-progress-step-${step}-mobile`}
                  className={`flex-1 h-2 rounded-full transition-colors ${
                    isActive ? 'bg-primary' : isCompleted ? 'bg-primary/40' : 'bg-border'
                  }`}
                />
              );
            })}
          </div>

          {/* Desktop: full labeled stepper */}
          <ol
            className="hidden md:flex items-center gap-1 overflow-x-auto pb-2 list-none m-0 p-0"
            aria-label="Add property progress"
            data-testid="wizard-progress-desktop"
          >
            {steps.map((step, index) => {
              const isActive = index === currentStepIndex;
              const isCompleted = index < currentStepIndex;
              return (
                <li
                  key={step}
                  className="flex items-center min-w-0"
                  aria-current={isActive ? 'step' : undefined}
                >
                  <div
                    data-testid={`wizard-progress-step-${step}`}
                    className={`flex flex-col items-center gap-1 px-2 py-1 rounded-lg min-h-[44px] min-w-[60px] transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : isCompleted
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <span className={`text-sm font-bold ${isActive ? 'text-primary-foreground' : ''}`}>
                      {index + 1}
                    </span>
                    <span className="text-xs whitespace-nowrap">{STEP_LABELS[step]}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      aria-hidden="true"
                      className={`h-0.5 flex-1 mx-1 min-w-[8px] ${isCompleted ? 'bg-primary/40' : 'bg-border'}`}
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      {bannerMessage && (
        <div className="mx-4 md:mx-6 lg:mx-8 mb-4 p-3 bg-destructive/5 border border-destructive/30 rounded-md text-sm text-destructive" role="alert" data-testid="create-property-error-message">
          {bannerMessage}
        </div>
      )}

      {/* Step content — sized to its own content (no min-h, unlike LeaseWizardPage: on a
          2-4 step wizard a fixed min-height left Back/Next floating far below the form). */}
      <div className="bg-card rounded-lg border border-border p-4 md:p-6 mx-4 md:mx-6 lg:mx-8" data-testid="create-property-form">
        {currentStep === 'type' && (
          <div data-testid="wizard-step-type">
            <h2 className="text-lg font-semibold text-foreground mb-2">Select Property Type</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              Choose whether this is a single-family or multi-family property. You can specify a sub-type (condo, townhouse, etc.) in the next step.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PRIMARY_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleTypeSelect(type.value)}
                  data-testid={`property-type-${type.value}`}
                  className="p-6 bg-card border-2 border-border rounded-lg cursor-pointer text-left hover:border-primary hover:bg-accent hover:-translate-y-0.5 hover:shadow-md transition-all min-h-[44px]"
                >
                  <h3 className="text-lg font-semibold text-foreground mb-2">{type.label}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed m-0">{type.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {currentStep === 'info' && (
          <div data-testid="wizard-step-info">
            <h2 className="text-lg font-semibold text-foreground mb-4">Property Info</h2>

            {portfoliosError && (
              <div
                role="alert"
                data-testid="portfolios-error-message"
                className="mb-4 p-3 bg-destructive/5 border border-destructive/30 rounded-md text-sm text-destructive flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <span>We couldn't load your portfolios, so a property can't be added yet.</span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={loadPortfolios}
                  disabled={isLoadingPortfolios}
                  data-testid="portfolios-retry-button"
                  className="min-h-[44px]"
                >
                  {isLoadingPortfolios ? 'Retrying...' : 'Retry'}
                </Button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 mb-0">
              {portfolios.length > 1 && (
                <div className="flex-1 mb-4">
                  <label htmlFor="portfolioId" className={labelClass}>Portfolio *</label>
                  <select
                    id="portfolioId"
                    data-testid="property-portfolio-select"
                    value={formData.portfolioId}
                    onChange={(e) => handleFormChange('portfolioId', e.target.value)}
                    className={inputClass + " bg-card"}
                    required
                  >
                    <option value="">Select a portfolio</option>
                    {portfolios.map((portfolio) => (
                      <option key={portfolio.id} value={portfolio.id}>
                        {portfolio.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex-1 mb-4">
                <label htmlFor="name" className={labelClass}>Property Name *</label>
                <input
                  id="name"
                  data-testid="property-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder={formData.propertyType === 'single_family' ? 'e.g., 123 Main St House' : 'e.g., Oakwood Apartments'}
                  className={inputClass}
                  required
                  maxLength={255}
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="subType" className={labelClass}>Sub-Type (Optional)</label>
              <select
                id="subType"
                data-testid="property-subtype-select"
                value={formData.subType}
                onChange={(e) => handleFormChange('subType', e.target.value)}
                className={inputClass + " bg-card"}
              >
                {SUB_TYPES.map((subType) => (
                  <option key={subType.value} value={subType.value}>
                    {subType.label} {subType.description && `- ${subType.description}`}
                  </option>
                ))}
              </select>
              <p className="text-xs text-primary italic mt-2 mb-0">
                {formData.propertyType === 'single_family'
                  ? 'e.g., If you own a single condo unit or apartment for rent'
                  : 'e.g., Apartment building, condo complex, or mixed-use property'}
              </p>
            </div>

            {formData.propertyType === 'multi_family' && (
              <div className="flex flex-col sm:flex-row gap-4 mb-0">
                <div className="flex-1 mb-4">
                  <label htmlFor="squareFeet" className={labelClass}>Square Feet (Optional)</label>
                  <input
                    id="squareFeet"
                    type="number"
                    value={formData.squareFeet}
                    onChange={(e) => handleFormChange('squareFeet', e.target.value)}
                    placeholder="2500"
                    className={inputClass}
                    min="0"
                    data-testid="property-squarefeet-input"
                  />
                </div>
                <div className="flex-1 mb-4">
                  <label htmlFor="yearBuilt" className={labelClass}>Year Built (Optional)</label>
                  <input
                    id="yearBuilt"
                    type="number"
                    value={formData.yearBuilt}
                    onChange={(e) => handleFormChange('yearBuilt', e.target.value)}
                    placeholder="2000"
                    className={inputClass}
                    min="1800"
                    max={new Date().getFullYear()}
                    data-testid="property-yearbuilt-input"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === 'address' && (
          <div data-testid="wizard-step-address">
            <h2 className="text-lg font-semibold text-foreground mb-4">Address</h2>

            <div className="mb-4">
              <label htmlFor="unitAddress1" className={labelClass}>Street Address *</label>
              <AddressAutocompleteInput
                id="unitAddress1"
                data-testid="property-address1-input"
                value={formData.unitAddress1}
                onChange={(v) => handleFormChange('unitAddress1', v)}
                onSuggestionSelect={handleAddressSuggestionSelect}
                placeholder="123 Main Street"
                className={inputClassFor('unitAddress1')}
                required
                {...errorProps('unitAddress1')}
              />
              {fieldError('unitAddress1', 'property-address1-error-message')}
            </div>

            <div className="mb-4">
              <label htmlFor="unitAddress2" className={labelClass}>Apt/Suite (Optional)</label>
              <input
                id="unitAddress2"
                data-testid="property-address2-input"
                type="text"
                value={formData.unitAddress2}
                onChange={(e) => handleFormChange('unitAddress2', e.target.value)}
                placeholder="Apt 4B"
                className={inputClassFor('unitAddress2')}
                {...errorProps('unitAddress2')}
              />
              {fieldError('unitAddress2', 'property-address2-error-message')}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-0">
              <div className="flex-1">
                <label htmlFor="unitCity" className={labelClass}>City *</label>
                <input
                  id="unitCity"
                  data-testid="property-city-input"
                  type="text"
                  value={formData.unitCity}
                  onChange={(e) => handleFormChange('unitCity', e.target.value)}
                  placeholder="San Francisco"
                  className={inputClassFor('unitCity')}
                  required
                  {...errorProps('unitCity')}
                />
                {fieldError('unitCity', 'property-city-error-message')}
              </div>
              <div className="w-full sm:w-[100px]">
                <label htmlFor="unitState" className={labelClass}>State *</label>
                <input
                  id="unitState"
                  data-testid="property-state-input"
                  type="text"
                  value={formData.unitState}
                  onChange={(e) => handleFormChange('unitState', e.target.value)}
                  placeholder="CA"
                  className={inputClassFor('unitState')}
                  required
                  maxLength={2}
                  {...errorProps('unitState')}
                />
                {fieldError('unitState', 'property-state-error-message')}
              </div>
              <div className="w-full sm:w-[100px]">
                <label htmlFor="unitZipCode" className={labelClass}>Zip *</label>
                <input
                  id="unitZipCode"
                  data-testid="property-zip-input"
                  type="text"
                  value={formData.unitZipCode}
                  onChange={(e) => handleFormChange('unitZipCode', e.target.value)}
                  placeholder="94102"
                  className={inputClassFor('unitZipCode')}
                  required
                  {...errorProps('unitZipCode')}
                />
                {fieldError('unitZipCode', 'property-zip-error-message')}
              </div>
            </div>
          </div>
        )}

        {currentStep === 'details' && (
          <div data-testid="wizard-step-details">
            <h2 className="text-lg font-semibold text-foreground mb-4">Details</h2>

            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex-1">
                <label htmlFor="bedrooms" className={labelClass}>Bedrooms *</label>
                <input
                  id="bedrooms"
                  data-testid="property-bedrooms-input"
                  type="number"
                  value={formData.bedrooms}
                  onChange={(e) => handleFormChange('bedrooms', e.target.value)}
                  placeholder="3"
                  className={inputClassFor('bedrooms')}
                  required
                  min="0"
                  max="20"
                  {...errorProps('bedrooms')}
                />
                {fieldError('bedrooms', 'property-bedrooms-error-message')}
              </div>
              <div className="flex-1">
                <label htmlFor="bathrooms" className={labelClass}>Bathrooms *</label>
                <input
                  id="bathrooms"
                  data-testid="property-bathrooms-input"
                  type="number"
                  step="0.5"
                  value={formData.bathrooms}
                  onChange={(e) => handleFormChange('bathrooms', e.target.value)}
                  placeholder="2"
                  className={inputClassFor('bathrooms')}
                  required
                  min="0"
                  {...errorProps('bathrooms')}
                />
                {fieldError('bathrooms', 'property-bathrooms-error-message')}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex-1">
                <label htmlFor="squareFeet" className={labelClass}>Square Feet (Optional)</label>
                <input
                  id="squareFeet"
                  type="number"
                  value={formData.squareFeet}
                  onChange={(e) => handleFormChange('squareFeet', e.target.value)}
                  placeholder="2500"
                  className={inputClassFor('squareFeet')}
                  min="0"
                  data-testid="property-squarefeet-input"
                  {...errorProps('squareFeet')}
                />
                {fieldError('squareFeet', 'property-squarefeet-error-message')}
              </div>
              <div className="flex-1">
                <label htmlFor="yearBuilt" className={labelClass}>Year Built (Optional)</label>
                <input
                  id="yearBuilt"
                  type="number"
                  value={formData.yearBuilt}
                  onChange={(e) => handleFormChange('yearBuilt', e.target.value)}
                  placeholder="2000"
                  className={inputClass}
                  min="1800"
                  max={new Date().getFullYear()}
                  data-testid="property-yearbuilt-input"
                />
              </div>
            </div>

            <div className="mb-2">
              <label htmlFor="notes" className={labelClass}>Notes (Optional)</label>
              <textarea
                id="notes"
                data-testid="property-notes-input"
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                placeholder="Any additional notes about this property..."
                className={inputClass}
                rows={3}
              />
            </div>
          </div>
        )}
      </div>

      {/* Desktop navigation buttons (inline) */}
      <div className="hidden md:flex justify-between items-center px-6 lg:px-8 mt-6" data-testid="wizard-desktop-action-bar">
        <Button
          type="button"
          variant="outline"
          data-testid="wizard-back-button"
          onClick={handleBack}
          disabled={isCreating}
          className="min-h-[44px]"
        >
          {currentStepIndex === 0 ? 'Cancel' : 'Back'}
        </Button>
        <Button
          type="button"
          data-testid={isLastStep ? 'create-property-submit' : 'wizard-next-button'}
          onClick={handleNext}
          disabled={!stepValid || isCreating}
          className="min-h-[44px]"
        >
          {isLastStep ? (isCreating ? 'Creating...' : 'Create Property') : 'Next'}
        </Button>
      </div>

      {/* Mobile: sticky bottom ActionBar */}
      <div
        className="md:hidden fixed bottom-[var(--bottom-tab-height)] left-0 right-0 z-40 bg-card border-t border-border px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]"
        data-testid="wizard-mobile-action-bar"
      >
        <div className="flex justify-between items-center gap-3">
          <Button
            type="button"
            variant="outline"
            data-testid="wizard-back-button-mobile"
            onClick={handleBack}
            disabled={isCreating}
            className="min-h-[44px] flex-1"
          >
            {currentStepIndex === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Button
            type="button"
            data-testid={isLastStep ? 'create-property-submit-mobile' : 'wizard-next-button-mobile'}
            onClick={handleNext}
            disabled={!stepValid || isCreating}
            className="min-h-[44px] flex-1"
          >
            {isLastStep ? (isCreating ? 'Creating...' : 'Create Property') : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AddPropertyWizardPage;
